// AddPersonSheet.jsx — Phase 2 — the new "Add Person" form (replaces the old
// person-modal add layout). Relation-type first (Child / Parent / Partnership /
// Honorary) with warnings + info per option, then the person's fields.
//
// UI-only: it collects input and calls onSubmit(payload, closeAfter). All the
// real integrity rules stay SERVER-SIDE in rpc_upsert_person (the caller wires
// the payload to it). Client-side we only *hint*.
//
// Two footer buttons (per spec): "Add" (submit + close + back to tree) and
// "Save and add another" (submit + reset + keep open). Reuses <DateField/> and
// renders published-template CUSTOM fields (customTemplate) into payload.customFields.
//
// Props:
//   people, currentPersonId, isAdmin, treeIsEmpty
//   initial        : { kind, sel } | null   (prefill, e.g. "add child under X")
//   customTemplate : { fields:[...] } | null
//   onSubmit       : (payload, closeAfter) => Promise|void
//   onClose        : () => void

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import DateField from "./DateField";
import { zonaLartPx } from "./zonaESigurt"; // DRITARET1 — koka nën prerjen e ekranit, si te Modal
import { tDyn } from "./serverText"; // tekstet e admin-it (emrat e opsioneve) → përkthim live pa deploy

// Mosha më e ulët e lejuar (me ligj) për një lidhje kur ajo krijohet nga fleta e
// shtimit — vlen për të gjitha llojet (martesë, fejesë, bashkëjetesë, partneritet).
// Kartela e personit NUK e zbaton: lidhjet e vjetra, të vërteta, futen atje.
const MOSHA_MIN_LIDHJE = 18;

// Opsionet e paracaktuara (fallback nëse admini s'ka konfiguruar ende). Kur
// `options` vjen nga DB (person_type_options), përdoret ai. `link_kind` cakton
// SJELLJEN (child/parent/partner/honorary); opsione të reja marrin një prej tyre.
const DEFAULT_OPTIONS = [
  { key: "child", label: "Add Child", description: "The preferred way to build the tree downwards.", icon: "＋", link_kind: "child", admin_only: false, recommended: true,
    info: "Select at least one parent. If both parents already exist in the tree, selecting both is recommended (there is no way to verify biological parentage, so a single parent is enough)." },
  // `admin_only: true` (19 gusht 2026) — përputhet me `rpc_add_parent_link`, që
  // kërkon `admin`. Më parë kjo listë rezervë thoshte `false` dhe redaktori e
  // plotësonte formularin kot: serveri e refuzonte gjithmonë në fund.
  { key: "parent", label: "Add Parent", description: "Use this when building the tree upwards.", icon: "↑", link_kind: "parent", admin_only: true, recommended: false,
    info: "Use this when building the tree upwards. You must select at least one existing child first — the new parent is linked to them. If the parent already has several children in the tree, select them all now to link them at once." },
  { key: "partner", label: "Add Family Member by Partnership", description: "Engagement, marriage, cohabitation or partnership.", icon: "⚭", link_kind: "partner", admin_only: false, recommended: false,
    info: "For people who join through engagement, marriage, cohabitation or partnership. You must choose the person they are connected to. They are not treated automatically as a blood descendant." },
  { key: "honorary", label: "Add Guest", description: "A guest added by an administrator — not part of the bloodline.", icon: "★", link_kind: "honorary", admin_only: true, recommended: false,
    info: "Administrators only. Guests (for research, historical documentation, fact-gathering) are not bound by the normal family-tree rules. A reason is required and is stored in the audit log; only an administrator can change it." },
];

export default function AddPersonSheet({ people = {}, currentPersonId, isAdmin = false, treeIsEmpty = false, rrenjaELejuar = false, initial = null, customTemplate = null, branchOf = null, options = null, canPostToFeed = false, onSubmit, onClose,
  // 5 shtator 2026 — VENDI. `BirthPlaceFields` jeton te `App.jsx` sepse varet
  // nga `Combobox`, `loadCities`, `countryOptions` e `COLORS` që janë atje.
  // Vjen si PROP, jo si import: `App.jsx` e importon këtë skedar, ndaj një
  // import i kundërt do të bënte cikël. `peopleAll` është pema e plotë (jo
  // lista e ngushtuar e zgjedhjes) — nga ajo dalin qytetet e vetë familjes.
  FushatEVendit = null, peopleAll = null, countries = [], canManage = false, addCountry = null, emriShtetit = null,
  // Emri me dallues për personat me emër të dyfishtë (shih `nameOf` më poshtë).
  emriDallues = null,
  // 6 shtator 2026 — «swipe back duhet të ndjekë logjikën e butonit ←».
  // Gjesti «prapa» i sistemit (Android) dhe back-u i shfletuesit e kapin App-i
  // dhe thërrasin `backRef.current()`: kthen `true` kur bëri një hap prapa
  // brenda fletës (formular → lista), `false` kur s'ka ku të kthehet — atëherë
  // App-i e mbyll fletën.
  backRef = null }) {
  const { t, i18n } = useTranslation();
  const list = useMemo(() => (Array.isArray(people) ? people : Object.values(people || {})), [people]);
  // `kind` = SJELLJA (link_kind: child/parent/partner/honorary). `selKey` =
  // çelësi i opsionit të zgjedhur (për titullin/info-n — dy opsione mund të
  // kenë të njëjtën sjellje).
  const [kind, setKind] = useState(initial?.kind || null);
  const [selKey, setSelKey] = useState(initial?.kind || null);
  // Faza 2 (kërkesë e userit) — flow me dy hapa si FAQE: "select" (zgjedh llojin)
  // → "form" (fushat), me buton Back. Jo zgjatje poshtë. Fillon te "form" nëse
  // ka initial (p.sh. "shto fëmijë nën X").
  const [step, setStep] = useState(initial?.kind ? "form" : "select");
  useEffect(() => {
    if (!backRef) return undefined;
    backRef.current = () => { if (step === "form") { setStep("select"); return true; } return false; };
    return () => { backRef.current = null; };
  }, [backRef, step]);
  const [q, setQ] = useState(""); // kërkim te picker-i (lista deri 2k+)
  const [openTip, setOpenTip] = useState(null);
  const [sel, setSel] = useState(initial?.sel || []);
  const [rel, setRel] = useState("marriage");
  const [relDate, setRelDate] = useState("");
  // "Posto në feed" — parazgjedhja është JO. Një person i shtuar është zakonisht
  // plotësim i pemës, jo lajm; lajmi është përjashtim dhe duhet zgjedhur.
  const [postToFeed, setPostToFeed] = useState(false);
  const [reason, setReason] = useState("");
  const [accessStart, setAccessStart] = useState("");
  const [accessEnd, setAccessEnd] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [maidenName, setMaidenName] = useState("");
  const [dob, setDob] = useState("");
  // DITA1 — viti mund të hamendësohet, muaji dhe dita jo. Kur ndizet, personi
  // nuk numërohet askund ku kërkohet dita ose muaji.
  const [dobPasakte, setDobPasakte] = useState(false);
  const [deceased, setDeceased] = useState(false);
  const [deathDate, setDeathDate] = useState("");
  const [gender, setGender] = useState("");
  const [birthPlace, setBirthPlace] = useState(""); // rezervë: vetëm nëse `FushatEVendit` nuk vjen
  // Vendlindja ishte NJË fushë teksti i lirë këtu, kurse redaktimi ka prej
  // kohësh shtetin, qytetin dhe fshatin veçmas. Pasoja (analiza e 4 shtatorit,
  // pika 2): personi i sapo-shtuar dilte me `birth_city`/`birth_country` bosh —
  // harta dhe kërkimi sipas qytetit s'e shihnin kurrë — dhe redaktimi i parë,
  // që e çmonton `birth_place` duke u nisur nga ato dy kolona, e gjente boshin
  // dhe ia FSHINTE vendlindjen në heshtje. Tani të dyja rrugët mbushin të
  // njëjtat fusha me të njëjtin komponent. Banimi aktual mungonte fare.
  const VENDI_BOSH = { birthCountry: "", birthCity: "", birthVillage: "", residenceCountry: "", residenceCity: "" };
  const [vendi, setVendi] = useState(VENDI_BOSH);
  const [customVals, setCustomVals] = useState({});
  const [busy, setBusy] = useState(false);
  // Kërkesë e userit — kur ruajtja dështon (çdo fushë, jo vetëm mysafiri): errori
  // shfaqet MBI formularin (jo poshtë/prapa fletës si toast i padukshëm), të
  // dhënat e mbushura NUK zhduken, dhe vetëm fusha bosh/gabim SKUQET.
  const [formError, setFormError] = useState("");
  const [errFields, setErrFields] = useState({});
  const bodyRef = useRef(null);
  const clearErr = (k) => setErrFields((e) => (e && e[k] ? { ...e, [k]: false } : e));
  const scrollTop = () => { if (bodyRef.current) bodyRef.current.scrollTop = 0; };

  // Swipe-për-t'u-kthyer (si te Modal) — kërkesë e userit: "swipe duhet të
  // funksionojë çdo herë". Rrëshqit DJATHTAS: nga forma → kthehu te zgjedhja;
  // te zgjedhja → mbyll fletën. Injoron lëvizjet kryesisht VERTIKALE (scroll).
  const touchStart = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const onTouchStart = (e) => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchMove = (e) => {
    if (!touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (Math.abs(dy) > Math.abs(dx) || dx < 0) return; // vertikale ose majtas → jo mbyllje
    setDragging(true); setDragX(dx);
  };
  const onTouchEnd = () => {
    if (dragX > 90) { if (step === "form") setStep("select"); else onClose?.(); }
    setDragX(0); setDragging(false); touchStart.current = null;
  };

  const customFields = (customTemplate && Array.isArray(customTemplate.fields) ? customTemplate.fields : [])
    .filter((f) => !f.is_core && (!f.visible_for || f.visible_for.length === 0 || (kind && f.visible_for.includes(kind))));

  const OPTIONS = (options && options.length ? options : DEFAULT_OPTIONS)
    .filter((o) => o.enabled !== false && (!o.admin_only || isAdmin))
    .map((o) => ({ k: o.key, label: tDyn(o.label), sub: tDyn(o.description || ""), info: tDyn(o.info || ""), icon: o.icon, adminOnly: !!o.admin_only, recommended: !!o.recommended, link_kind: o.link_kind || o.key }));
  // RRENJA1 (13 shtator 2026) — pronari: «adminit duhet t'i lejohet ta krijojë
  // paraardhësin e parë; dy persona pa prindër vetëm në fillim, kur familja ka
  // vetëm një anëtar». Opsion virtual, jo nga regjistri i Admin Center-it:
  // shfaqet vetëm kur App-i thotë se lejohet (admin, pa rrënjë, ≤ 1 person).
  // Serveri e pranon me `p_is_root` (FAZA2) dhe e kufizon në një për familje.
  if (rrenjaELejuar && isAdmin) {
    OPTIONS.unshift({ k: "root", label: t("Add the First Known Ancestor"), sub: t("The oldest person you know — the root of the tree. Only now, while the family has one member."),
      info: t("The ancestor is created without parents. Afterwards edit your own card and set your parents, so the tree runs from the ancestor down to you."),
      icon: "🌳", adminOnly: true, recommended: true, link_kind: "root" });
  }
  const selOpt = OPTIONS.find((o) => o.k === selKey) || null;

  // 5 shtator 2026 — te kjo listë zgjidhet prindi/fëmija/partneri, dhe me dy
  // «Arben Krasniqi» njeriu s'e dinte cilin po zgjidhte. `emriDallues` vjen si
  // prop nga `App.jsx` (aty jeton harta e emrave që përsëriten) dhe shton
  // atësinë ose vitin VETËM te ata që kanë emër të dyfishtë.
  const nameOf = (p) => (emriDallues ? emriDallues(p) : "") || `${p.first_name || ""} ${p.last_name || ""}`.trim() || t("(unnamed)");
  const initials = (p) => (`${(p.first_name || "?")[0] || ""}${(p.last_name || "")[0] || ""}`).toUpperCase();

  function toggleSel(id, single) {
    clearErr("sel");
    setSel((cur) => (single ? [id] : cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  // Validim me THEKSIM fushash — kthen cilat fusha janë bosh/gabim (që të
  // skuqen), në vend që butoni thjesht të rrijë i çaktivizuar pa shpjegim.
  function validate() {
    const f = {};
    if (!firstName.trim()) f.firstName = true;
    if ((kind === "child" || kind === "parent") && sel.length < 1) f.sel = true;
    if (kind === "partner" && sel.length !== 1) f.sel = true;
    if (kind === "honorary" && !reason.trim()) f.reason = true;
    // Fushat custom të shënuara me `*`. Ylli premtonte fushë të detyrueshme,
    // por asgjë s'e detyronte — as klienti, as serveri: formulari dorëzohej
    // bosh pa asnjë ankesë. Tani kontrollohen si çdo fushë tjetër e detyrueshme,
    // dhe skuqen së bashku me to.
    customFields.forEach((cf) => {
      if (!cf.required) return;
      const v = customVals[cf.key];
      const empty = v == null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);
      if (empty) f["custom:" + cf.key] = true;
    });
    // 6 shtator 2026 — DATA E LIDHJES = DATËLINDJA E PARTNERIT EKZISTUES.
    // Pesë çifte të shtuara në një ditë dolën me datë martese të barabartë me
    // datëlindjen e bashkëshortit që ekzistonte më parë. Askush s'martohet
    // ditën që lind; kjo datë hyri gabim te fusha «Data e lidhjes» dhe baza e
    // pranoi. Tani kontrollohet këtu (mesazh i qartë, pa udhëtim te serveri)
    // DHE te baza (MARTESA1 — trigger, që t'i mbyllë rrugën çdo dere tjetër).
    if (kind === "partner" && relDate && sel.length === 1) {
      const partneri = list.find((x) => x.id === sel[0]);
      const lindjaEPartnerit = (partneri && partneri.birth_date) || "";
      if ((lindjaEPartnerit && relDate <= lindjaEPartnerit) || (dob && relDate <= dob)) {
        f.relDate = true;
        return { ok: false, fields: f, message: t("The wedding date cannot be on or before the date of birth of either spouse.") };
      }
      // Kontrolli 2 — mosha: në ditën e lidhjes të dy duhet të kenë mbushur
      // MOSHA_MIN_LIDHJE vjeç. Vlen vetëm KËTU, në krijim; kartela e personit
      // e lejon pas krijimit (pemët e vjetra kanë martesa më të hershme, të vërteta).
      const moshaNe = (lindja, data) => {
        const l = new Date(lindja), d = new Date(data);
        if (isNaN(l) || isNaN(d)) return null;
        let m = d.getFullYear() - l.getFullYear();
        if (d.getMonth() < l.getMonth() || (d.getMonth() === l.getMonth() && d.getDate() < l.getDate())) m -= 1;
        return m;
      };
      const mP = lindjaEPartnerit ? moshaNe(lindjaEPartnerit, relDate) : null;
      const mR = dob ? moshaNe(dob, relDate) : null;
      if ((mP !== null && mP < MOSHA_MIN_LIDHJE) || (mR !== null && mR < MOSHA_MIN_LIDHJE)) {
        f.relDate = true;
        return { ok: false, fields: f, message: t("Both partners must be at least 18 on the date of the relationship. If this is an older, real relationship, save the person first and set the date from their profile card.") };
      }
    }
    const ok = Object.keys(f).length === 0;
    return { ok, fields: f, message: ok ? "" : t("Please fill in the required fields highlighted below.") };
  }

  // `birth_place` mbetet teksti i lexueshëm «Fshat, Qytet, Shtet» — ndërtohet
  // saktësisht si te `saveForm`, që të dyja rrugët të shkruajnë të njëjtën
  // formë dhe çmontimi te redaktimi të mos gabojë. Ruhet EMRI i shtetit, jo
  // kodi; kodi shkon veçmas te `birth_country`.
  function vendlindjaTekst() {
    if (!FushatEVendit) return birthPlace.trim();
    const fshati = (vendi.birthVillage || "").trim();
    const qyteti = (vendi.birthCity || "").trim();
    const kodi = (vendi.birthCountry || "").trim();
    const shteti = (emriShtetit ? emriShtetit(kodi) : "") || kodi;
    return [fshati, qyteti, shteti].filter(Boolean).join(", ");
  }

  function buildPayload() {
    const mysafir = kind === "honorary";
    return {
      kind,
      person: {
        first_name: firstName.trim(), last_name: lastName.trim(), maiden_name: maidenName.trim(),
        birth_date: dob || null, birth_date_approx: !!dobPasakte, death_date: deceased ? (deathDate || null) : null,
        gender: gender || null, birth_place: mysafir ? "" : vendlindjaTekst(),
        // Fushat e strukturuara — pa to harta, kërkimi sipas qytetit dhe
        // kartela «Migrimi» nuk e shohin personin e sapo-shtuar.
        birth_city: mysafir ? "" : (vendi.birthCity || "").trim(),
        birth_country: mysafir ? "" : (vendi.birthCountry || "").trim(),
        residence_country: mysafir ? "" : (vendi.residenceCountry || "").trim(),
        residence_city: mysafir ? "" : (vendi.residenceCity || "").trim(),
        external_tree_url: "", // fusha u hoq nga formulari (5 shtator 2026)
      },
      links: {
        parentIds: kind === "child" ? sel : [],
        childIds: kind === "parent" ? sel : [],
        partnerId: kind === "partner" ? sel[0] : null,
        relationshipType: kind === "partner" ? rel : null,
        relationshipDate: kind === "partner" ? (relDate || null) : null,
      },
      postToFeed: canPostToFeed ? postToFeed : false,
      honorary: kind === "honorary" ? { reason: reason.trim(), accessStart: accessStart || null, accessEnd: accessEnd || null } : null,
      customFields: customVals,
      requesterId: currentPersonId,
    };
  }

  function resetForNext() {
    // Mban kind + sel (p.sh. shto disa fëmijë nën të njëjtët prindër); pastron pjesën tjetër.
    setFirstName(""); setLastName(""); setMaidenName(""); setDob(""); setDobPasakte(false); setDeceased(false); setDeathDate("");
    setGender(""); setBirthPlace(""); setVendi(VENDI_BOSH); setCustomVals({}); setReason(""); setAccessStart(""); setAccessEnd(""); setPostToFeed(false);
    setRelDate(""); // 6 shtator 2026: data e lidhjes s'i takon personit tjetër
  }

  async function submit(closeAfter) {
    // 1) Validim klienti — nëse mungon diçka, SKUQ fushat + shfaq mesazhin lart,
    //    pa e pastruar asgjë.
    const v = validate();
    if (!v.ok) { setErrFields(v.fields); setFormError(v.message); scrollTop(); return; }
    setErrFields({}); setFormError("");
    setBusy(true);
    try {
      await onSubmit?.(buildPayload(), closeAfter);
      // Sukses — VETËM te "ruaj & shto tjetër" pastrohet formulari.
      if (!closeAfter) resetForNext();
    } catch (e) {
      // Dështim serveri — mbaj TË GJITHA të dhënat, shfaq errorin MBI formular.
      // 5 shtator 2026 — mesazhi vjen nga Postgres dhe është GJITHMONË anglisht;
      // deri tani shfaqej fjalë për fjalë, pra brenda aplikacionit shqip dilte
      // një paragraf anglisht. `tDyn` e përkthen kur teksti është çelës i njohur,
      // përndryshe e kalon si është dhe e regjistron që admini ta përkthejë pa
      // deploy — e njëjta rrugë si për tekstet e tjera të serverit.
      setFormError((e && e.message) ? tDyn(e.message) : t("Saving failed. Please try again."));
      scrollTop();
    } finally { setBusy(false); }
  }

  const S = STYLES;
  const cin = (v, set, extra) => (<input style={{ ...S.in, ...extra }} value={v} onChange={(e) => set(e.target.value)} />);

  // Kërkim te picker-i — praktik për lista shumë të mëdha (2k+): filtro me emër
  // dhe shfaq deri 100, me numërues "X nga Y".
  // Përfshi EDHE personin aktual — ti mund të shtosh fëmijën/prindin/partnerin
  // TËND, ku ti je pika e lidhjes (më parë vetja përjashtohej gabimisht).
  const pickBase = list;
  const needle = q.trim().toLowerCase();
  const pickFiltered = needle ? pickBase.filter((p) => nameOf(p).toLowerCase().includes(needle)) : pickBase;
  const pickShown = pickFiltered.slice(0, 100);
  const searchBox = (
    <input
      style={{ ...S.in, marginBottom: 8 }}
      placeholder={t("Search people…")}
      value={q}
      onChange={(e) => setQ(e.target.value)}
    />
  );
  const countNote = pickFiltered.length > pickShown.length
    ? <div style={S.hint}>{t("Showing {{shown}} of {{total}} — refine your search.", { shown: pickShown.length, total: pickFiltered.length })}</div>
    : null;
  // "Pin" — të zgjedhurit rrinë lart si chips (gjithmonë të dukshëm), që kur
  // regjistron disa fëmijë/prindër nën të njëjtët, mos të kërkosh sërish.
  const selPeople = sel.map((id) => list.find((x) => x.id === id)).filter(Boolean);
  const selectedChips = selPeople.length > 0 ? (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
      {selPeople.map((p) => (
        <span key={p.id} style={S.pinChip}>
          📌 {nameOf(p)}
          <span style={{ cursor: "pointer", fontWeight: 800, marginLeft: 2 }} onClick={() => setSel(sel.filter((x) => x !== p.id))}>×</span>
        </span>
      ))}
    </div>
  ) : null;

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div
        style={{ ...S.sheet, transform: dragX ? `translateX(${dragX}px)` : undefined, opacity: dragX ? Math.max(0.4, 1 - dragX / 320) : 1, transition: dragging ? "none" : "transform .22s ease, opacity .22s ease" }}
        role="dialog"
        aria-label={t("Add Person")}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* DRITARET1 — zona e sigurt matet në render, jo në ngarkim të modulit (atëherë s'është matur ende) */}
        <div style={{ ...S.head, paddingTop: Math.max(16, zonaLartPx()) }}>
          {step === "form" && (
            <button aria-label={t("Back")} style={S.x} onClick={() => setStep("select")}>←</button>
          )}
          <div style={S.title}>{step === "form" && selOpt ? selOpt.label : t("Add Person")}</div>
          <button aria-label={t("Close")} style={S.x} onClick={() => onClose?.()}>✕</button>
        </div>

        {formError && (
          <div style={S.errorBanner} role="alert">
            <span aria-hidden style={{ marginTop: 1 }}>⚠️</span>
            <div style={{ flex: 1 }}>{formError}</div>
            <button aria-label={t("Dismiss")} onClick={() => setFormError("")} style={S.errX}>✕</button>
          </div>
        )}

        <div ref={bodyRef} style={S.body}>
          {step === "select" && (
          <>
          <div style={S.warn} role="note">
            <span aria-hidden style={{ marginTop: 1 }}>⚠️</span>
            <div><b>{t("Important:")}</b> {t("The option you choose determines how this person is connected to the family tree. Please read the information provided for each option before continuing.")}</div>
          </div>

          {treeIsEmpty && (
            <div style={S.hintBox}>
              {t("This is the first person in the tree — the First Known Ancestor. They can exist without a parent or child. Only an administrator can create them, and there can be only one.")}
            </div>
          )}

          {OPTIONS.map((o) => (
            <div key={o.k} style={S.opt} onClick={() => { setSelKey(o.k); setKind(o.link_kind); setSel([]); setQ(""); setStep("form"); }}>
              <div style={S.optRow}>
                <div style={{ ...S.oic, ...(o.adminOnly ? S.oicB : null) }} aria-hidden>{o.icon || (o.adminOnly ? "★" : o.link_kind === "parent" ? "↑" : o.link_kind === "partner" ? "⚭" : "＋")}</div>
                <div style={S.oname}>
                  {o.label}
                  <button aria-label={t("More info")} style={S.info} onClick={(e) => { e.stopPropagation(); setOpenTip(openTip === o.k ? null : o.k); }}>i</button>
                </div>
                {o.recommended && <span style={S.chip}>{t("Recommended")}</span>}
                {o.adminOnly && <span style={S.admin}>{t("Admin only")}</span>}
              </div>
              <div style={S.osub}>{o.sub}</div>
              {openTip === o.k && <div style={S.tip}>{o.info}</div>}
            </div>
          ))}
          </>
          )}

          {step === "form" && kind && (
            <div style={S.form}>
              {selOpt && <div style={{ ...S.tip, marginTop: 0, marginBottom: 14 }}>{selOpt.info}</div>}
              {(kind === "child" || kind === "parent") && (
                <>
                  <div style={S.flabel}>{kind === "child" ? t("Parent(s) *") : t("Existing child(ren) *")}</div>
                  {selectedChips}
                  {searchBox}
                  <div style={{ ...S.pickList, ...(errFields.sel ? S.inErr : null) }}>
                    {pickShown.map((p) => (
                      <div key={p.id} style={{ ...S.pick, ...(sel.includes(p.id) ? S.pickOn : null) }} onClick={() => toggleSel(p.id, false)}>
                        <div style={S.ph}>{initials(p)}</div>
                        <div><div style={S.pname}>{nameOf(p)}</div><div style={S.pmeta}>{branchOf ? branchOf(p) : (p.branch || "core")}</div></div>
                        <div style={{ ...S.cbox, ...(sel.includes(p.id) ? S.cboxOn : null) }}>{sel.includes(p.id) ? "✓" : ""}</div>
                      </div>
                    ))}
                  </div>
                  {countNote}
                  {sel.length > 0 && <div style={S.hint}>{t("Selected: {{count}}", { count: sel.length })}</div>}
                  <div style={S.hint}>ⓘ {kind === "child" ? t("One parent is enough — if both are in the tree, selecting both is recommended.") : t("Add Parent needs at least one existing child selected.")}</div>
                </>
              )}

              {kind === "partner" && (
                <>
                  <div style={S.flabel}>{t("Connected to *")}</div>
                  {selectedChips}
                  {searchBox}
                  <div style={{ ...S.pickList, ...(errFields.sel ? S.inErr : null) }}>
                    {pickShown.map((p) => (
                      <div key={p.id} style={{ ...S.pick, ...(sel[0] === p.id ? S.pickOn : null) }} onClick={() => toggleSel(p.id, true)}>
                        <div style={S.ph}>{initials(p)}</div>
                        <div><div style={S.pname}>{nameOf(p)}</div><div style={S.pmeta}>{branchOf ? branchOf(p) : (p.branch || "core")}</div></div>
                        <div style={{ ...S.radio, ...(sel[0] === p.id ? S.radioOn : null) }} />
                      </div>
                    ))}
                  </div>
                  {countNote}
                  <div style={{ ...S.field, marginTop: 14 }}>
                    <div style={S.flabel}>{t("Relationship type *")}</div>
                    <select style={S.in} value={rel} onChange={(e) => setRel(e.target.value)}>
                      <option value="marriage">{t("Marriage")}</option>
                      <option value="engagement">{t("Engagement")}</option>
                      <option value="cohabitation">{t("Cohabitation")}</option>
                      <option value="partnership">{t("Partnership")}</option>
                    </select>
                  </div>
                  <div style={S.field}>
                    {/* 6 shtator 2026: etiketa thotë qartë «Data e martesës» kur lloji është martesë —
                        «Data e lidhjes» u lexua si datëlindje dhe u shkrua data e lindjes së partnerit. */}
                    <div style={S.flabel}>{rel === "marriage" ? t("Wedding date") : t("Date of the relationship")}</div>
                    <DateField value={relDate} onChange={setRelDate} inputStyle={{ ...S.in, ...(errFields.relDate ? S.inErr : null) }} ariaLabel={rel === "marriage" ? t("Wedding date") : t("Date of the relationship")} />
                  </div>
                </>
              )}

              {kind === "honorary" && (
                <>
                  <div style={S.field}>
                    <div style={S.flabel}>{t("Reason * — stored in the audit log, editable by admins only")}</div>
                    <textarea style={{ ...S.in, minHeight: 66, fontFamily: "inherit", resize: "vertical", ...(errFields.reason ? S.inErr : null) }} value={reason} onChange={(e) => { setReason(e.target.value); clearErr("reason"); }} />
                  </div>
                  <div style={S.two}>
                    <div style={S.field}><div style={S.flabel}>{t("Access start")}</div><DateField value={accessStart} onChange={setAccessStart} inputStyle={S.in} ariaLabel={t("Access start")} /></div>
                    <div style={S.field}><div style={S.flabel}>{t("Access end")}</div><DateField value={accessEnd} onChange={setAccessEnd} inputStyle={S.in} ariaLabel={t("Access end")} /></div>
                  </div>
                  <div style={S.hint}>ⓘ {t("On expiry the account is blocked, not deleted; the person stays visible until an admin removes them.")}</div>
                </>
              )}

              {/* common fields */}
              <div style={S.two}>
                <div style={S.field}><div style={S.flabel}>{t("First name *")}</div><input style={{ ...S.in, ...(errFields.firstName ? S.inErr : null) }} value={firstName} onChange={(e) => { setFirstName(e.target.value); clearErr("firstName"); }} /></div>
                <div style={S.field}><div style={S.flabel}>{t("Last name")}</div>{cin(lastName, setLastName)}</div>
              </div>
              <div style={S.field}><div style={S.flabel}>{t("Maiden name")}</div>{cin(maidenName, setMaidenName)}</div>
              <div style={S.two}>
                <div style={S.field}>
                  <div style={S.flabel}>{t("Date of birth")}</div>
                  <DateField value={dob} onChange={setDob} inputStyle={S.in} ariaLabel={t("Date of birth")} />
                  {dob && (
                    <label style={{ ...S.check, alignItems: "flex-start", marginTop: 8 }}>
                      <input type="checkbox" checked={dobPasakte} onChange={(e) => setDobPasakte(e.target.checked)} style={{ marginTop: 3 }} />
                      <span style={{ fontSize: 13.5, lineHeight: 1.4 }}>{t("The date of birth is not exact")}</span>
                    </label>
                  )}
                </div>
                <div style={S.field}><div style={S.flabel}>{t("Gender")}</div>
                  <select style={S.in} value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value=""></option><option value="F">{t("Female")}</option><option value="M">{t("Male")}</option>
                  </select>
                </div>
              </div>
              <label style={S.check}><input type="checkbox" checked={deceased} onChange={(e) => setDeceased(e.target.checked)} /> {t("Deceased")}</label>
              {deceased && (
                <div style={S.field}><div style={S.flabel}>{t("Date of death")}</div><DateField value={deathDate} onChange={setDeathDate} inputStyle={S.in} ariaLabel={t("Date of death")} /></div>
              )}
              {kind !== "honorary" && (FushatEVendit ? (
                <>
                  <div style={S.field}>
                    <div style={S.flabel}>{t("Birthplace")}</div>
                    <div style={S.grid2}>
                      <FushatEVendit form={vendi} setForm={setVendi} people={peopleAll || people}
                        countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language} />
                    </div>
                  </div>
                  {/* Banimi aktual — i njëjti komponent pa rreshtin e fshatit.
                      Te shtimi mungonte krejt: mbushej vetëm nëse dikush e
                      hapte personin sërish për redaktim. */}
                  <div style={S.field}>
                    <div style={S.flabel}>{t("Current residence (optional)")}</div>
                    <div style={S.grid2}>
                      <FushatEVendit form={vendi} setForm={setVendi} people={peopleAll || people}
                        countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language}
                        celesat={{ village: null, city: "residenceCity", country: "residenceCountry" }} meFshat={false} />
                    </div>
                  </div>
                </>
              ) : (
                <div style={S.field}><div style={S.flabel}>{t("Birthplace")}</div>{cin(birthPlace, setBirthPlace)}</div>
              ))}
              {/* ⚠ 5 shtator 2026 — «Lidhje me një pemë tjetër» u hoq edhe këtu.
                  Premtonte një urë që s'ekziston: kush nuk është anëtar te pema
                  tjetër, s'e hap dot atë link. Lidhja mes familjeve bëhet me
                  kodet e familjes dhe me sinkronizimin e personit. */}

              {/* custom template fields */}
              {customFields.length > 0 && (
                <div style={{ borderTop: `1px dashed ${V("border", "#eae3d8")}`, paddingTop: 14, marginTop: 4 }}>
                  {customFields.map((f) => {
                    const val = customVals[f.key] ?? "";
                    const set = (v) => { setCustomVals((cf) => ({ ...cf, [f.key]: v })); clearErr("custom:" + f.key); };
                    const bad = !!errFields["custom:" + f.key];
                    return (
                      <div key={f.key} style={S.field}>
                        <div style={{ ...S.flabel, ...(bad ? { color: "#b23a3a" } : null) }}>{f.label}{f.required ? " *" : ""}</div>
                        {f.type === "boolean" ? (
                          <label style={S.check}><input type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} /> {f.label}</label>
                        ) : f.type === "select" ? (
                          <select style={S.in} value={val} onChange={(e) => set(e.target.value)}>
                            <option value="">—</option>
                            {(Array.isArray(f.options) ? f.options : []).map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : f.type === "date" ? (
                          <DateField value={val} onChange={set} inputStyle={S.in} ariaLabel={f.label} />
                        ) : (
                          <input type={f.type === "number" ? "number" : "text"} style={S.in} value={val} onChange={(e) => set(e.target.value)} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 8 shtator 2026 (pronari) — kutiza është PJESË e formularit, elementi
                  i fundit që rrëshqet bashkë me fushat, jo e ngulitur nën ekran ku
                  zinte vend gjatë gjithë kohës. Shfaqet vetëm kur çelësi `new_member`
                  te Admin Center është i ndezur — përndryshe do t'i ofronte dikujt
                  një zgjedhje që platforma e ka fikur. */}
              {canPostToFeed && (
                <label style={S.postimi}>
                  <input type="checkbox" checked={postToFeed} onChange={() => setPostToFeed((v) => !v)} style={{ marginTop: 3, width: 18, height: 18 }} />
                  <span>
                    <span style={{ fontSize: 14.5, fontWeight: 600 }}>{t("Post it as news on the front page: this member joined the family")}</span>
                    <span style={{ display: "block", fontSize: 13.5, opacity: 0.7, marginTop: 2 }}>
                      {t("Off unless you tick it.")}
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        {/* 8 shtator 2026 — dy butona, një rresht. «Mbrapa» u hoq: e bën shigjeta
            lart majtas dhe rrëshqitja anash; me dy butona rreshti s'thyhet më
            në dy dhe fundi zë gjysmën e hapësirës. */}
        {step === "form" && (
          <div style={S.foot}>
            <button style={{ ...S.ghost, flex: 1, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => submit(false)}>
              {t("Save and add another")}
            </button>
            <button style={{ ...S.btn, opacity: busy ? 0.5 : 1, cursor: busy ? "default" : "pointer" }} disabled={busy} onClick={() => submit(true)}>
              {busy ? t("Saving…") : t("Add")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const V = (name, fb) => `var(--${name}, ${fb})`;
const STYLES = {
  overlay: { position: "fixed", inset: 0, background: "rgba(20,16,10,.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "stretch", justifyContent: "center", zIndex: 1000 },
  sheet: { width: "100%", maxWidth: 560, background: V("surface", "#fff"), borderRadius: 0, boxShadow: "0 0 40px rgba(0,0,0,.25)", display: "flex", flexDirection: "column", height: "100%" },
  // DRITARET1 (13 shtator 2026) — «←» dhe «✕» dilnin nën orën e iPhone-it:
  // koka kishte 16px fikse, pa zonën e sigurt. I njëjti numër i matur si te
  // Modal-i i App.jsx (zonaLartPx), jo një vlerë e re.
  head: { display: "flex", alignItems: "center", gap: 10, padding: "16px 18px", borderBottom: `1px solid ${V("border", "#eae3d8")}` },
  title: { fontFamily: V("font-display", "'Fraunces',Georgia,serif"), fontSize: 20, fontWeight: 600, flex: 1, color: V("text", "#241f18") },
  // 6 shtator 2026 — zona e prekjes 44px (ishte 34): «X» dhe «←» në telefon.
  x: { width: 44, height: 44, borderRadius: 12, fontSize: 18, border: `1px solid ${V("border", "#eae3d8")}`, background: V("surface2", "#f4efe8"), color: V("text2", "#6f6555"), cursor: "pointer" },
  body: { padding: 18, overflowY: "auto", overflowX: "hidden" },
  warn: { display: "flex", gap: 11, background: V("warnBg", "#fdf3e6"), border: `1px solid ${V("warnBorder", "#f0d19a")}`, color: V("warnText", "#8a5a12"), borderRadius: 13, padding: "12px 14px", fontSize: 14, lineHeight: 1.5, marginBottom: 16 },
  hintBox: { background: V("accentSoft", "#e6f0e9"), border: `1px solid ${V("accentBorder", "#bcd8c8")}`, color: V("accent", "#3f6f52"), borderRadius: 12, padding: "11px 14px", fontSize: 13.5, lineHeight: 1.5, marginBottom: 14 },
  opt: { border: `1px solid ${V("border", "#eae3d8")}`, borderRadius: 14, padding: 14, marginBottom: 12, cursor: "pointer", background: V("surface", "#fff") },
  optSel: { borderColor: V("accent", "#3f6f52"), boxShadow: `0 0 0 3px ${V("accentSoft", "#e6f0e9")}` },
  optRow: { display: "flex", alignItems: "center", gap: 12 },
  oic: { width: 42, height: 42, borderRadius: 12, background: V("accentSoft", "#e6f0e9"), color: V("accent", "#3f6f52"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flex: "none" },
  oicB: { background: V("bronzeSoft", "#f5ecdd"), color: V("bronze", "#b0813f") },
  oname: { fontWeight: 700, fontSize: 16, flex: 1, display: "flex", alignItems: "center", gap: 7, color: V("text", "#241f18") },
  info: { width: 20, height: 20, borderRadius: 999, border: `1.5px solid ${V("muted", "#9c9384")}`, background: "transparent", color: V("muted", "#9c9384"), fontSize: 12.5, fontWeight: 800, cursor: "pointer", lineHeight: 1 },
  osub: { color: V("text2", "#6f6555"), fontSize: 13.5, marginTop: 7, lineHeight: 1.5, paddingLeft: 54 },
  tip: { marginTop: 10, background: V("surface2", "#f4efe8"), border: `1px solid ${V("border", "#eae3d8")}`, borderRadius: 11, padding: "11px 13px", fontSize: 13.5, lineHeight: 1.55, color: V("text2", "#6f6555") },
  chip: { fontSize: 12.5, fontWeight: 600, padding: "4px 11px", borderRadius: 999, background: V("accentSoft", "#e6f0e9"), color: V("accent", "#3f6f52"), border: `1px solid ${V("accentBorder", "#bcd8c8")}` },
  admin: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: V("bronze", "#b0813f"), background: V("bronzeSoft", "#f5ecdd"), padding: "2px 8px", borderRadius: 999 },
  form: { marginTop: 6, borderTop: `1px dashed ${V("border", "#eae3d8")}`, paddingTop: 18 },
  flabel: { fontSize: 13, fontWeight: 600, color: V("text2", "#6f6555"), margin: "0 0 6px" },
  field: { marginBottom: 14 },
  two: { display: "flex", gap: 10 },
  // Shteti dhe qyteti krah për krah, fshati poshtë — e njëjta rrjetë si te
  // formulari i redaktimit, që të dy formularët të duken si një.
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: V("text", "#241f18"), margin: "2px 0 12px", cursor: "pointer" },
  // 8 shtator 2026 — 16 px: nën këtë prag iOS-i e zmadhon vetë ekranin kur fokusohet fusha, dhe zmadhimi mbetet.
  in: { width: "100%", boxSizing: "border-box", border: `1px solid ${V("border", "#eae3d8")}`, background: V("surface", "#fff"), borderRadius: 11, padding: "11px 13px", fontSize: 16, color: V("text", "#241f18"), outline: "none" },
  inErr: { borderColor: V("danger", "#c0392b"), boxShadow: `0 0 0 2px ${V("dangerSoft", "rgba(192,57,43,.16)")}` },
  errorBanner: { display: "flex", alignItems: "flex-start", gap: 10, background: V("dangerBg", "#fdecea"), borderBottom: `1px solid ${V("dangerBorder", "#e6a49c")}`, color: V("dangerText", "#a12a1c"), padding: "12px 16px", fontSize: 14, lineHeight: 1.45 },
  errX: { width: 26, height: 26, flex: "none", borderRadius: 8, border: "none", background: "transparent", color: V("dangerText", "#a12a1c"), fontSize: 14, cursor: "pointer" },
  pickList: { border: `1px solid ${V("border", "#eae3d8")}`, borderRadius: 12, overflow: "hidden", maxHeight: 220, overflowY: "auto" },
  pick: { display: "flex", alignItems: "center", gap: 11, padding: "10px 13px", borderBottom: `1px solid ${V("border", "#eae3d8")}`, cursor: "pointer" },
  pickOn: { background: V("accentSoft", "#e6f0e9") },
  ph: { width: 34, height: 34, borderRadius: 999, border: `2px solid ${V("accent", "#3f6f52")}`, background: V("surface2", "#f4efe8"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, color: V("text2", "#6f6555") },
  pname: { fontWeight: 600, fontSize: 14.5, color: V("text", "#241f18") },
  pmeta: { color: V("muted", "#9c9384"), fontSize: 12.5 },
  cbox: { width: 20, height: 20, borderRadius: 6, border: `2px solid ${V("border", "#eae3d8")}`, marginLeft: "auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 },
  cboxOn: { background: V("accent", "#3f6f52"), borderColor: V("accent", "#3f6f52") },
  radio: { width: 20, height: 20, borderRadius: 999, border: `2px solid ${V("border", "#eae3d8")}`, marginLeft: "auto" },
  radioOn: { borderColor: V("accent", "#3f6f52"), boxShadow: `inset 0 0 0 4px ${V("accent", "#3f6f52")}` },
  hint: { fontSize: 12.5, color: V("muted", "#9c9384"), marginTop: 6 },
  pinChip: { display: "inline-flex", alignItems: "center", gap: 2, padding: "4px 10px", borderRadius: 999, background: V("accentSoft", "#e6f0e9"), color: V("accent", "#3f6f52"), border: `1px solid ${V("accentBorder", "#bcd8c8")}`, fontSize: 13.5, fontWeight: 600 },
  foot: { display: "flex", gap: 8, padding: "12px 18px", borderTop: `1px solid ${V("border", "#eae3d8")}` },
  postimi: { display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 0 4px", marginTop: 6, borderTop: `1px dashed ${V("border", "#eae3d8")}`, cursor: "pointer" },
  ghost: { padding: "12px 14px", borderRadius: 12, border: `1px solid ${V("border", "#eae3d8")}`, background: V("surface", "#fff"), color: V("text", "#241f18"), fontWeight: 600, fontSize: 15, cursor: "pointer" },
  btn: { flex: 1, padding: 12, borderRadius: 12, border: "none", background: V("accent", "#3f6f52"), color: "#fff", fontWeight: 700, fontSize: 15 },
};
